/**
 * @fileoverview Custom select dropdown component
 * @module ctl-select
 * @version 1.0.0
 * 
 * @description
 * A custom select dropdown component that provides a styled alternative to the native
 * HTML select element. Features a clean design, smooth animations, keyboard navigation,
 * and better styling control than native selects.
 * 
 * The component renders options from slotted <option> elements, supports single
 * selection with typeahead and keyboard navigation, and provides full keyboard
 * accessibility (arrow keys, Enter, Escape). It integrates seamlessly with forms
 * and supports validation states.
 * 
 * @example
 * <!-- Basic select with slotted options -->
 * <ctl-select 
 *   name="country" 
 *   placeholder="Select a country"
 *   value="us">
 *   <option value="us">United States</option>
 *   <option value="ca">Canada</option>
 *   <option value="mx">Mexico</option>
 * </ctl-select>
 * 
 * @example
 * <!-- Select with slotted options -->
 * <ctl-select name="size" placeholder="Choose size">
 *   <option value="small">Small</option>
 *   <option value="medium" selected>Medium</option>
 *   <option value="large">Large</option>
 * </ctl-select>
 * 
 * @example
 * <!-- Disabled select -->
 * <ctl-select 
 *   placeholder="Unavailable" 
 *   disabled>
 * </ctl-select>
 * 
 * @example
 * <!-- Select with validation -->
 * <ctl-form-field label="Country" required invalid error="Please select a country">
 *   <ctl-select name="country" placeholder="Select..."></ctl-select>
 * </ctl-form-field>
 * 
 * @cssprop --ctl-field-height - Field height (from token system)
 * @cssprop --ctl-input-padding-x - Horizontal padding (from token system)
 * @cssprop --ctl-radius-md - Border radius (from token system)
 * @cssprop --ctl-space-2 - Gap between elements (from token system)
 * @cssprop --ctl-z-dropdown - Popover z-index (from token system)
 * @cssprop --ctl-shadow-dropdown - Popover shadow (from token system)
 */

import { dispatchEvent } from '../../../utils/events.js';
import { shadowQuery, shadowQueryAll } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .wrap{
    position: relative;
  }

  .trigger{
    all: unset;
    box-sizing: border-box;
    width: 100%;
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    height: var(--ctl-field-height);
    padding: 0 var(--ctl-input-padding-x);
    border-radius: var(--ctl-radius-md);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    transition: border-color var(--ctl-transition-normal),
                box-shadow var(--ctl-transition-normal),
                background var(--ctl-transition-normal),
                opacity var(--ctl-transition-normal);
    cursor: pointer;
  }

  :host([invalid]) .trigger{
    border-color: var(--ctl-color-danger);
  }

  :host(:focus-within) .trigger{
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-shadow-focus);
  }

  :host([disabled]) .trigger{
    opacity: var(--ctl-opacity-disabled);
    background: color-mix(in srgb, var(--ctl-color-text) 2%, var(--ctl-color-surface));
    cursor: not-allowed;
    pointer-events: none;
  }

  .slot{
    display:inline-flex;
    align-items:center;
    color: var(--ctl-color-muted);
  }
  .slot:empty{ display:none; }

  .value{
    flex: 1;
    min-width: 0;
    font-size: var(--ctl-font-size-base);
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .placeholder{
    color: var(--ctl-color-muted);
  }

  .chev{
    width: var(--ctl-size-icon-sm);
    height: var(--ctl-size-icon-sm);
    color: var(--ctl-color-muted);
    display:inline-flex;
    align-items:center;
    justify-content:center;
  }

  .btn{
    all: unset;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width: var(--ctl-size-icon-btn);
    height: var(--ctl-size-icon-btn);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    color: var(--ctl-color-muted);
    transition: background var(--ctl-transition-fast),
                color var(--ctl-transition-fast);
  }
  
  .btn:hover{
    background: color-mix(in srgb, var(--ctl-color-text) 6%, transparent);
    color: var(--ctl-color-text);
  }
  
  .btn:focus-visible{
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  .clear{ display:none; }
  :host([clearable]) .clear{ display:inline-flex; }
  :host([_hasvalue]) .clear{ display:inline-flex; }
  :host(:not([_hasvalue])) .clear{ display:none; }

  /* Popover */
  .popover{
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + var(--ctl-space-2));
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    box-shadow: var(--ctl-shadow-dropdown);
    padding: var(--ctl-space-2);
    z-index: var(--ctl-z-dropdown);
    display: none;
    max-height: 280px;
    overflow: auto;
  }

  :host([open]) .popover{ display:block; }

  .listbox{
    display:flex;
    flex-direction:column;
    gap: var(--ctl-space-1);
  }

  .opt{
    display:flex;
    align-items:center;
    justify-content:space-between;
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    user-select: none;
    font-size: var(--ctl-font-size-base);
    line-height: 1.2;
  }

  .opt[aria-disabled="true"]{
    opacity: .5;
    cursor: not-allowed;
  }

  .opt[aria-selected="true"]{
    background: color-mix(in srgb, var(--ctl-color-focus) 10%, transparent);
  }

  .opt[data-active="true"]{
    outline: var(--ctl-focus-ring-width) solid color-mix(in srgb, var(--ctl-color-focus) 35%, transparent);
    outline-offset: calc(-1 * var(--ctl-focus-ring-width));
    background: color-mix(in srgb, var(--ctl-color-focus) 8%, transparent);
  }

  .check{
    width: 16px;
    height: 16px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-color-focus);
    opacity: 0;
  }
  .opt[aria-selected="true"] .check{ opacity: 1; }

  /* Hide light DOM options but keep them for authoring */
  ::slotted(option){
    display:none;
  }
</style>

<div class="wrap">
  <button
    id="trigger"
    class="trigger"
    type="button"
    aria-haspopup="listbox"
    aria-expanded="false"
  >
    <span class="slot" part="prefix"><slot name="prefix"></slot></span>

    <span id="valueEl" class="value"></span>

    <button class="btn clear" id="clearBtn" type="button" aria-label="Clear" title="Clear">×</button>

    <span class="slot" part="suffix"><slot name="suffix"></slot></span>

    <span class="chev" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="16" height="16">
        <path fill="currentColor" d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.2l3.9-3.9a1 1 0 1 1 1.4 1.4l-4.6 4.6a1 1 0 0 1-1.4 0L6.7 10.7a1 1 0 0 1 0-1.4z"></path>
      </svg>
    </span>
  </button>

  <div id="popover" class="popover" part="popover">
    <div id="listbox" class="listbox" role="listbox"></div>
  </div>

  <slot id="optionsSlot"></slot>
</div>
`;

function uid(prefix = "ctl") {
  return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
}

function normalize(v) {
  return v == null ? "" : String(v);
}

function isPrintableKey(e) {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

/**
 * Custom Select Web Component
 * 
 * A styled dropdown select component with keyboard navigation and form integration.
 * Provides better styling and UX than native HTML select elements.
 * 
 * @class CtlSelect
 * @extends HTMLElement
 * 
 * @attr {string} name - Form field name for submission
 * @attr {string} value - Currently selected value
 * @attr {string} placeholder - Placeholder text shown when no value is selected
 * @attr {boolean} disabled - Whether select is disabled
 * @attr {boolean} required - Whether select is required
 * @attr {boolean} invalid - Whether select is in invalid state
 * @attr {boolean} open - Controls dropdown open state
 * @attr {boolean} clearable - Shows clear button when value is selected
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot - Default slot for native option elements
 * @slot prefix - Content before the selected value (icons, text)
 * @slot suffix - Content after the selected value (before chevron)
 * 
 * @fires ctl-change - Custom event with detail.value property
 * @fires ctl-open - Dispatched when dropdown opens
 * @fires ctl-close - Dispatched when dropdown closes
 * @fires ctl-clear - Dispatched when clear button is clicked
 * 
 * @csspart prefix - The prefix slot container
 * @csspart suffix - The suffix slot container
 * @csspart popover - The dropdown popover container
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-radius - Border radius (default: 10px)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted/placeholder text color (default: #6b7280)
 * @cssprop --ctl-color-border - Border color (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Focus ring and highlight color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Error/invalid color (default: #dc2626)
 * @cssprop --ctl-color-shadow - Shadow color (default: rgba(0,0,0,.14))
 * @cssprop --ctl-field-height - Field height (default: 40px)
 * @cssprop --ctl-pad-x - Horizontal padding (default: 12px)
 * @cssprop --ctl-gap - Gap between elements (default: 8px)
 * @cssprop --ctl-popover-radius - Dropdown border radius (default: 12px)
 * @cssprop --ctl-popover-pad - Dropdown padding (default: 6px)
 * @cssprop --ctl-option-pad - Option padding (default: 10px 10px)
 * @cssprop --ctl-option-radius - Option border radius (default: 10px)
 */
export class CtlSelect extends HTMLElement {
  static formAssociated = true;

  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "name", "value", "placeholder",
      "disabled", "required", "invalid",
      "open", "clearable",
      // ARIA passthrough to trigger
      "aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._internals = this.attachInternals ? this.attachInternals() : null;

    this._trigger = this.shadowRoot.getElementById("trigger");
    this._valueEl = this.shadowRoot.getElementById("valueEl");
    this._popover = this.shadowRoot.getElementById("popover");
    this._listbox = this.shadowRoot.getElementById("listbox");
    this._clearBtn = this.shadowRoot.getElementById("clearBtn");
    this._optionsSlot = this.shadowRoot.getElementById("optionsSlot");

    this._idBase = uid("ctl-select");
    this._listboxId = `${this._idBase}-listbox`;
    this._triggerId = `${this._idBase}-trigger`;

    this._activeIndex = -1;
    this._typeahead = "";
    this._typeaheadTimer = null;

    // Initialize _options to empty array to prevent undefined errors during initialization
    // Options will be populated from slotted <option> elements via _rebuildOptions()
    this._options = [];

    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onClearClick = this._onClearClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("placeholder")) this.setAttribute("placeholder", "Select…");

    if (!this.id) this.id = this._idBase;

    this._trigger.id = this._triggerId;
    this._listbox.id = this._listboxId;

    this._trigger.addEventListener("click", this._onTriggerClick);
    this._trigger.addEventListener("keydown", this._onKeyDown);
    this._clearBtn.addEventListener("click", this._onClearClick);
    this._optionsSlot.addEventListener("slotchange", this._onSlotChange);

    this._rebuildOptions();
    this._syncAll();
  }

  disconnectedCallback() {
    this._trigger.removeEventListener("click", this._onTriggerClick);
    this._trigger.removeEventListener("keydown", this._onKeyDown);
    this._clearBtn.removeEventListener("click", this._onClearClick);
    this._optionsSlot.removeEventListener("slotchange", this._onSlotChange);
    this._removeDocListener();
  }

  attributeChangedCallback() {
    this._syncAll();
  }

  // ---- Public API ----
  get value() { return this.getAttribute("value") ?? ""; }
  set value(v) { this.setAttribute("value", normalize(v)); }

  get name() { return this.getAttribute("name"); }
  set name(v) { v == null ? this.removeAttribute("name") : this.setAttribute("name", String(v)); }

  focus(opts) { this._trigger.focus(opts); }

  // ---- Internals ----
  _syncAll() {
    this._syncAria();
    this._syncValueText();
    this._syncOpenState();
    this._syncHasValue();
    this._syncFormValue();
  }

  _syncAria() {
    const open = this.hasAttribute("open");

    this._trigger.setAttribute("aria-expanded", open ? "true" : "false");
    this._trigger.setAttribute("aria-controls", this._listboxId);

    // passthrough aria-* to trigger
    for (const a of ["aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"]) {
      if (this.hasAttribute(a)) this._trigger.setAttribute(a, this.getAttribute(a));
      else this._trigger.removeAttribute(a);
    }

    // If host [invalid] but no explicit aria-invalid passthrough, reflect it
    if (!this.hasAttribute("aria-invalid")) {
      if (this.hasAttribute("invalid")) this._trigger.setAttribute("aria-invalid", "true");
      else this._trigger.removeAttribute("aria-invalid");
    }

    // listbox selection
    // Guard against _options not being initialized yet
    if (this._options && this._options.length > 0) {
      const selectedIndex = this._options.findIndex((o) => o.value === this.value);
      if (selectedIndex >= 0) {
        const optId = `${this._idBase}-opt-${selectedIndex}`;
        this._trigger.setAttribute("aria-activedescendant", open ? optId : "");
      } else {
        this._trigger.removeAttribute("aria-activedescendant");
      }
    } else {
      this._trigger.removeAttribute("aria-activedescendant");
    }
  }

  _syncHasValue() {
    const has = !!this.value;
    if (has) this.setAttribute("_hasvalue", "");
    else this.removeAttribute("_hasvalue");
  }

  _syncFormValue() {
    if (this._internals?.setFormValue) {
      const v = this.value || "";
      this._internals.setFormValue(v);
    }
  }

  _syncValueText() {
    // Guard against _options not being initialized yet
    if (!this._options || this._options.length === 0) {
      const placeholder = this.getAttribute("placeholder") || "Select…";
      if (this._valueEl) {
        this._valueEl.textContent = placeholder;
        this._valueEl.classList.add("placeholder");
      }
      return;
    }
    
    const selected = this._options.find((o) => o.value === this.value);
    const placeholder = this.getAttribute("placeholder") || "Select…";

    if (!selected) {
      this._valueEl.textContent = placeholder;
      this._valueEl.classList.add("placeholder");
    } else {
      this._valueEl.textContent = selected.label;
      this._valueEl.classList.remove("placeholder");
    }

    // reflect selection UI
    this._renderListbox();
  }

  _syncOpenState() {
    const open = this.hasAttribute("open");
    if (open) this._addDocListener();
    else this._removeDocListener();
  }

  _addDocListener() {
    document.addEventListener("pointerdown", this._onDocPointerDown, true);
  }
  _removeDocListener() {
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
  }

  _onDocPointerDown(e) {
    const path = e.composedPath ? e.composedPath() : [];
    if (path.includes(this) || path.includes(this.shadowRoot)) return;
    this.close();
  }

  _onTriggerClick(e) {
    // Ignore clicks on clear button (it sits inside trigger)
    if (e.composedPath && e.composedPath().includes(this._clearBtn)) return;

    if (this.hasAttribute("disabled")) return;
    this.toggle();
  }

  _onClearClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.hasAttribute("disabled")) return;
    if (!this.hasAttribute("clearable")) return;
    this._setValue("", { emit: true });
  }

  _onSlotChange() {
    this._rebuildOptions();
    this._syncAll();
  }

  _getLightOptions() {
    // Options are authored as <option value="x">Label</option> in light DOM
    const assigned = this._optionsSlot.assignedNodes({ flatten: true }) || [];
    // assignedNodes returns a mix; filter for elements named OPTION
    const optionEls = [];
    for (const n of assigned) {
      if (n.nodeType === Node.ELEMENT_NODE && n.tagName === "OPTION") optionEls.push(n);
    }
    // If nothing assigned, query directly as fallback (for some Storybook DOM arrangements)
    if (!optionEls.length) {
      return Array.from(this.querySelectorAll("option"));
    }
    return optionEls;
  }

  _rebuildOptions() {
    const optionEls = this._getLightOptions();
    this._options = optionEls.map((el, i) => ({
      value: el.getAttribute("value") ?? el.textContent ?? "",
      label: (el.textContent || "").trim() || el.getAttribute("value") || "",
      disabled: el.hasAttribute("disabled"),
      _el: el,
      _id: `${this._idBase}-opt-${i}`
    }));

    // If current value no longer exists, clear it
    if (this.value && !this._options.some((o) => o.value === this.value)) {
      this._setValue("", { emit: false });
    }

    this._renderListbox();
  }

  _renderListbox() {
    const selectedValue = this.value;

    this._listbox.replaceChildren(
      ...this._options.map((o, idx) => {
        const div = document.createElement("div");
        div.className = "opt";
        div.id = o._id;
        div.setAttribute("role", "option");
        div.setAttribute("aria-selected", o.value === selectedValue ? "true" : "false");
        div.setAttribute("aria-disabled", o.disabled ? "true" : "false");
        div.dataset.index = String(idx);

        div.innerHTML = `
          <span>${escapeHtml(o.label)}</span>
          <span class="check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M9.2 16.6 4.9 12.3a1 1 0 1 1 1.4-1.4l2.9 2.9 8-8a1 1 0 0 1 1.4 1.4l-8.7 8.7a1 1 0 0 1-1.4 0z"></path>
            </svg>
          </span>
        `;

        div.addEventListener("pointerdown", (e) => {
          // Prevent losing focus before click selection
          e.preventDefault();
        });

        div.addEventListener("click", () => {
          if (o.disabled) return;
          this._setValue(o.value, { emit: true });
          this.close();
          this._trigger.focus();
        });

        return div;
      })
    );

    // Maintain active index
    const selectedIndex = this._options.findIndex((o) => o.value === selectedValue);
    this._activeIndex = selectedIndex >= 0 ? selectedIndex : this._firstEnabledIndex();
    this._applyActive();
  }

  _firstEnabledIndex() {
    const idx = this._options.findIndex((o) => !o.disabled);
    return idx >= 0 ? idx : -1;
  }

  _applyActive() {
    const nodes = Array.from(this._listbox.querySelectorAll(".opt"));
    nodes.forEach((n) => n.removeAttribute("data-active"));

    if (this._activeIndex < 0 || this._activeIndex >= nodes.length) return;

    const active = nodes[this._activeIndex];
    active.setAttribute("data-active", "true");
    // Ensure it is visible when list scrolls
    active.scrollIntoView({ block: "nearest" });
  }

  _moveActive(delta) {
    if (!this._options.length) return;

    let i = this._activeIndex;
    for (let step = 0; step < this._options.length; step++) {
      i = (i + delta + this._options.length) % this._options.length;
      if (!this._options[i].disabled) {
        this._activeIndex = i;
        this._applyActive();
        return;
      }
    }
  }

  _selectActive() {
    if (this._activeIndex < 0) return;
    const o = this._options[this._activeIndex];
    if (!o || o.disabled) return;
    this._setValue(o.value, { emit: true });
  }

  _handleTypeahead(char) {
    clearTimeout(this._typeaheadTimer);
    this._typeahead += char.toLowerCase();
    this._typeaheadTimer = setTimeout(() => (this._typeahead = ""), 500);

    const start = Math.max(this._activeIndex, 0);
    const all = this._options;

    const matchIndex = (from) => {
      for (let k = 0; k < all.length; k++) {
        const idx = (from + k) % all.length;
        const o = all[idx];
        if (o.disabled) continue;
        if (o.label.toLowerCase().startsWith(this._typeahead)) return idx;
      }
      return -1;
    };

    let idx = matchIndex(start);
    if (idx === -1) idx = matchIndex(0);
    if (idx !== -1) {
      this._activeIndex = idx;
      this._applyActive();
    }
  }

  _onKeyDown(e) {
    if (this.hasAttribute("disabled")) return;

    const open = this.hasAttribute("open");

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) this.open();
      this._moveActive(+1);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) this.open();
      this._moveActive(-1);
      return;
    }

    if (e.key === "Home") {
      e.preventDefault();
      if (!open) this.open();
      this._activeIndex = this._firstEnabledIndex();
      this._applyActive();
      return;
    }

    if (e.key === "End") {
      e.preventDefault();
      if (!open) this.open();
      // last enabled
      for (let i = this._options.length - 1; i >= 0; i--) {
        if (!this._options[i].disabled) {
          this._activeIndex = i;
          break;
        }
      }
      this._applyActive();
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        this.open();
      } else {
        this._selectActive();
        this.close();
      }
      return;
    }

    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        this.close();
      } else if (this.hasAttribute("clearable") && this.value) {
        e.preventDefault();
        this._setValue("", { emit: true });
      }
      return;
    }

    if (isPrintableKey(e)) {
      if (!open) this.open();
      this._handleTypeahead(e.key);
    }
  }

  _setValue(value, { emit }) {
    const v = normalize(value);
    const prev = this.value;

    // Reflect to attribute
    if (v) this.setAttribute("value", v);
    else this.removeAttribute("value");

    this._syncHasValue();
    this._syncFormValue();
    this._syncValueText();

    if (emit && v !== prev) {
      // Guard against _options not being initialized yet
      const selected = (this._options && this._options.length > 0) 
        ? this._options.find((o) => o.value === v) || null 
        : null;

      dispatchEvent(this, "ctl-change", {
        name: this.getAttribute("name") || null,
        value: v || "",
        label: selected ? selected.label : null
      });
    }
  }

  toggle() {
    if (this.hasAttribute("open")) this.close();
    else this.open();
  }

  open() {
    if (this.hasAttribute("open")) return;
    this.setAttribute("open", "");
    this._renderListbox();
    this._applyActive();
    dispatchEvent(this, "ctl-open", {});
  }

  close() {
    if (!this.hasAttribute("open")) return;
    this.removeAttribute("open");
    dispatchEvent(this, "ctl-close", {});
  }

  // ---- Form-associated custom element hooks ----
  formResetCallback() {
    // Reset to empty unless author provided a value attribute (then back to that)
    const attr = this.getAttribute("value") ?? "";
    // If value attr exists, it’s already applied; otherwise clear
    if (!attr) this._setValue("", { emit: false });
  }

  formStateRestoreCallback(state) {
    // Restore state if browser provides it
    if (typeof state === "string") this._setValue(state, { emit: false });
  }
}

// small escape helper for option labels
function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

customElements.define("ctl-select", CtlSelect);
